import { useId, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ErrorBox, useApi } from './kit';

// Before a campaign's emails: "This is how I understand what you sell" (from the approved business-context facts,
// each line with its sources) and the campaign's 2-3 outreach angles, the recommended one first. Both are shown as
// cards in the chat; the angles also on the campaign page. A correction is a business-context proposal (POST
// /v1/context/proposals), accepted by an owner, admin or approver like any other; choosing an angle drafts the emails
// around it, or, on a built campaign whose first emails wait, rewrites them and asks for the review again.

type Source = { fact_id: string; topic: string; text: string; from: 'website' | 'document' | 'you' | 'other'; title: string | null; url: string | null };
type ULine = { text: string; sources: Source[] };
export type UnderstandingData = {
  understanding: { id: string; confirmed_at: string | null; what: ULine | null; who: ULine | null; problem: ULine | null; proof: ULine[]; unknowns: string[] } | null;
  due: boolean; stale: boolean; facts: number;
};

const FROM: Record<Source['from'], string> = { website: 'your website', document: 'a document', you: 'what you told Outlevo', other: 'your business context' };
const sourceLabel = (s: Source) => (s.title ? `${s.title}` : FROM[s.from]);

function Sources({ sources }: { sources: Source[] }) {
  if (!sources.length) return null;
  return <span className="muted small"> · from {sources.map((s, i) => <span key={s.fact_id}>{i ? ', ' : ''}{s.url ? <a href={s.url} target="_blank" rel="noreferrer">{sourceLabel(s)}</a> : sourceLabel(s)}</span>)}</span>;
}

/** The parts of the summary a correction can be about, and the business-context topic it is proposed under. */
const PARTS = [
  { key: 'what', label: 'What you sell', topic: 'product' },
  { key: 'who', label: 'Who it is for', topic: 'customer' },
  { key: 'problem', label: 'The problem it solves', topic: 'positioning' },
  { key: 'proof', label: 'Proof or what sets you apart', topic: 'proof' },
  { key: 'other', label: 'Something else', topic: 'profile' },
] as const;

export function Understanding({ onDone }: { onDone?: (text: string) => void } = {}) {
  const uid = useId();
  const qc = useQueryClient();
  const q = useApi<UnderstandingData>('/v1/context/understanding');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [fixing, setFixing] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const d = q.data;
  if (!d) return q.error ? <ErrorBox error={q.error} /> : <p className="muted small" role="status">Reading your business context…</p>;
  const u = d.understanding;
  if (!u) return <p className="muted small">There are no business-context facts yet to say what you sell. Ask the assistant to read your website, or add a document.</p>;
  const run = async (work: () => Promise<string>) => {
    setErr(null); setBusy(true);
    try { const text = await work(); setSaid(text); setFixing(false); onDone?.(text.startsWith('You confirmed') ? 'Confirmed what you sell' : 'Proposed a correction to what you sell'); await qc.invalidateQueries(); } catch (e) { setErr(e); } finally { setBusy(false); }
  };
  const correct = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    const f = new FormData(ev.currentTarget);
    const part = PARTS.find((p) => p.key === f.get('part')) ?? PARTS[4];
    const line = part.key === 'what' ? u.what : part.key === 'who' ? u.who : part.key === 'problem' ? u.problem : null;
    const replaces = line?.sources[0]?.fact_id;
    void run(async () => {
      await api('/v1/context/proposals', { method: 'POST', body: { topic: part.topic, text: String(f.get('text') ?? '').trim(), ...(replaces ? { statement_id: replaces } : {}), rationale: 'Corrected on "This is how I understand what you sell"' } });
      return 'Proposed to Business context: an owner, admin or approver accepts it there, and the summary and the emails use it from then on.';
    });
  };
  const row = (label: string, l: ULine | null) => (
    <li key={label}><b>{label}</b> {l ? <>{l.text}<Sources sources={l.sources} /></> : <span className="muted">Not in your facts yet</span>}</li>
  );
  return (
    <section className="und" aria-label="How Outlevo understands what you sell">
      <ul className="und-list">
        {row('What you sell:', u.what)}
        {row('For whom:', u.who)}
        {row('The problem:', u.problem)}
        {u.proof.length ? <li><b>Proof:</b><ul>{u.proof.map((p) => <li key={p.text}>{p.text}<Sources sources={p.sources} /></li>)}</ul></li> : row('Proof:', null)}
      </ul>
      {u.unknowns.length ? <p className="small"><b>What I don't know yet:</b> {u.unknowns.join('; ')}.</p> : null}
      {said ? <p className="wg-done" role="status">✓ {said}</p> : u.confirmed_at ? <p className="wg-done" role="status">✓ You confirmed this.</p> : null}
      {fixing ? (
        <form className="wg-form" onSubmit={correct}>
          <label htmlFor={`${uid}-p`}>What is off<select id={`${uid}-p`} name="part" defaultValue="what">{PARTS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}</select></label>
          <label htmlFor={`${uid}-t`}>What is true, in your words<textarea id={`${uid}-t`} name="text" required minLength={3} maxLength={600} rows={3} /></label>
          <p className="muted small">It becomes a proposal in Business context: an owner, admin or approver accepts it, as any change to your facts.</p>
          <div className="wg-actions">
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Proposing…' : 'Propose the correction'}</button>
            <button type="button" className="btn" disabled={busy} onClick={() => setFixing(false)}>Cancel</button>
          </div>
        </form>
      ) : (
        <div className="wg-actions">
          {!u.confirmed_at && !said ? <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void run(async () => {
            await api('/v1/context/understanding/confirm', { method: 'POST', body: { id: u.id } }); return 'You confirmed this.';
          })}>That's right</button> : null}
          <button type="button" className="btn" disabled={busy} onClick={() => { setFixing(true); setErr(null); }}>Correct something</button>
        </div>
      )}
      {err ? <ErrorBox error={err} doing="save that" /> : null}
    </section>
  );
}

type Angle = {
  id: string; rank: number; recommended: boolean; kind: 'pain' | 'trigger' | 'proof' | 'other'; title: string; hook: string; why: string;
  facts: Array<{ id: string; topic: string; text: string }>; segment: string | null;
  sample: { company: string; domain: string; subject: string; body: string } | null;
};
export type AnglesData = {
  campaign_id: string; campaign_name: string; status: string;
  chosen: { id: string; title: string; chosen_by: 'person' | 'autopilot' } | null;
  switch: { allowed: boolean; effect: 'redraft' | 'reopen_first_batch' | null; note: string };
  angles: Angle[];
};
const KIND: Record<Angle['kind'], string> = { pain: 'Pain', trigger: 'Why now', proof: 'Proof', other: 'Angle' };

/** compact: the card in the chat; otherwise the campaign page's section. */
export function CampaignAngles({ campaignId, compact = false }: { campaignId: string; compact?: boolean }) {
  const qc = useQueryClient();
  const q = useApi<AnglesData>(`/v1/campaigns/${campaignId}/angles`);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);
  const a = q.data;
  if (!a) return q.error ? <ErrorBox error={q.error} /> : compact ? <p className="muted small" role="status">Reading the angles…</p> : null;
  const run = async (key: string, work: () => Promise<string | null>) => {
    setErr(null); setBusy(key);
    try { setNote(await work()); await qc.invalidateQueries(); } catch (e) { setErr(e); } finally { setBusy(null); }
  };
  const choose = (x: Angle) => {
    if (a.switch.effect === 'reopen_first_batch' && !confirm(`Rewrite this campaign's emails around “${x.title}”? The first emails you approved wait for your review again.`)) return;
    void run(x.id, async () => (await api<{ note: string }>(`/v1/campaigns/${campaignId}/angle`, { method: 'POST', body: { angle_id: x.id } })).note);
  };
  const draft = () => run('draft', async () => { await api(`/v1/campaigns/${campaignId}/angles`, { method: 'POST', body: {} }); return null; });
  if (!a.angles.length) {
    if (!a.switch.allowed && !compact) return null;
    return <section className="ang" aria-label="Outreach angles">
      {compact ? null : <h2 className="fb-title">Angle</h2>}
      <p className="muted small">2–3 ways to pitch this campaign to its segment, each with a sample email. You pick one; the emails are written around it.</p>
      {a.switch.allowed ? <div className="wg-actions"><button type="button" className="btn" disabled={Boolean(busy)} onClick={() => void draft()}>{busy === 'draft' ? 'Working out angles…' : 'Suggest angles'}</button></div> : null}
      {err ? <ErrorBox error={err} doing="suggest angles" /> : null}
    </section>;
  }
  return (
    <section className={`ang${compact ? ' ang-compact' : ''}`} aria-label="Outreach angles">
      {compact ? null : <h2 className="fb-title">Angle</h2>}
      {a.chosen ? <p className="small">Emails written around <b>“{a.chosen.title}”</b>{a.chosen.chosen_by === 'autopilot' ? ' — the recommended angle, used because nobody picked one.' : '.'}</p>
        : <p className="small">Pick how to pitch “{a.campaign_name}”. The emails are written around the angle you choose.</p>}
      <ol className="ang-list">
        {a.angles.map((x) => {
          const inUse = a.chosen?.id === x.id;
          return (
            <li key={x.id} className={`ang-item${inUse ? ' on' : ''}`}>
              <div className="fb-head">
                <span><b>{x.title}</b> <span className="chip">{KIND[x.kind]}</span>{x.recommended ? <> <span className="chip next">Recommended</span></> : null}</span>
                {inUse ? <span className="chip done">In use</span> : null}
              </div>
              <p className="ang-hook">“{x.hook}”</p>
              <p className="muted small">{x.why}</p>
              {x.facts.length ? <details><summary className="small">Rests on {x.facts.length === 1 ? '1 fact' : `${x.facts.length} facts`}</summary><ul className="small tight">{x.facts.map((f) => <li key={f.id}>{f.text}</li>)}</ul></details> : null}
              {x.sample ? <details><summary className="small">Sample email for {x.sample.company}</summary><div className="msg-subject">{x.sample.subject}</div><pre className="body">{x.sample.body}</pre></details> : null}
              {a.switch.allowed && !(inUse && a.chosen?.chosen_by === 'person') ? <div className="wg-actions">
                <button type="button" className={`btn${x.recommended && !a.chosen ? ' btn-primary' : ''}`} disabled={Boolean(busy)} onClick={() => choose(x)} aria-label={`Use the angle ${x.title}`}>
                  {busy === x.id ? 'Writing the emails…' : inUse ? 'Keep this angle' : 'Use this angle'}
                </button>
              </div> : null}
            </li>
          );
        })}
      </ol>
      {note ? <p className="wg-done" role="status">✓ {note}</p> : null}
      <p className="muted small">{a.switch.note}</p>
      {a.switch.allowed ? <div className="wg-actions"><button type="button" className="link small" disabled={Boolean(busy)} onClick={() => void draft()}>{busy === 'draft' ? 'Working out angles…' : 'Suggest other angles'}</button></div> : null}
      {err ? <ErrorBox error={err} doing="use that angle" /> : null}
    </section>
  );
}
